import { ROOT_SERVER_URL } from '../config';
import { collectQueryParams, request } from '../utils';

export type DataFeedInventorySummaryRow = {
  id: string;
  name: string;
  venueId?: string | null;
  awsLakeAvailable: boolean;
  minAwsLakeAvailable: string;
  maxAwsLakeAvailable: string;
  awsArbitratedPcapsAvailable: boolean;
  minAwsArbitratedPcapsAvailable: string;
  maxAwsArbitratedPcapsAvailable: string;
  awsRawFeedsAvailable: boolean;
  minAwsRawFeedsAvailable: string;
  maxAwsRawFeedsAvailable: string;
};

export type DataFeedRow = {
  name: string;
  notes: string;
  paths: string[];
  approvalServiceIds: string[];
  venueId?: string | null;
};

export type DataFeedInventoryRegressionRow = {
  affectedPermissions: string[];
  deletedDates: string[];
  fullyDeletedDataFeeds: string[];
  regressedMonths: string[];
};

export function getServiceDataFeeds(serviceId: string) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds?serviceId=${serviceId}`);
}

export function getDataFeedInventorySummary(): Promise<DataFeedInventorySummaryRow[]> {
  return request(`${ROOT_SERVER_URL}/api/data-feeds`);
}

export function refreshAndGetDataFeedInventorySummary() {
  return request(`${ROOT_SERVER_URL}/api/data-feeds?forceRefresh=true`);
}

export function getDataFeed(dataFeedId: string) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/${dataFeedId}`);
}

export function getDataFeedApprovalServices(dataFeedId: string) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/${dataFeedId}/feed-approval-services`);
}

export function getDataFeedsApprovalService(serviceId: string) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/${serviceId}/service-approval`);
}

export function getDataFeedInventoryServiceStatus() {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/services`);
}

export function getDataFeedInventoryRegression(serviceId: string): Promise<DataFeedInventoryRegressionRow> {
  return request(`${ROOT_SERVER_URL}/api/data-feed-inventory-regression?serviceId=${serviceId}`);
}

export function addDataFeed(data: Partial<DataFeedRow>) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds`, {
    data,
    method: 'POST',
  });
}

export function updateDataFeed(dataFeedId: string, changes: Partial<DataFeedRow>) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/${dataFeedId}`, {
    data: changes,
    method: 'PUT',
  });
}

export function resolveDataFeedInventoryRegression(regression: DataFeedInventoryRegressionRow, serviceId: string) {
  return request(`${ROOT_SERVER_URL}/api/data-feed-inventory-regression/${serviceId}`, {
    data: { regression },
    method: 'PUT',
  });
}

export function getPathDetails(path: string) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/path-details?path=${encodeURIComponent(path)}`);
}

export function updatePathDetails(pathId: string, data: { startDate: string; endDate: string }) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/path-details/${pathId}`, {
    data,
    method: 'POST',
  });
}

export function updateMetadata(
  dataFeedId: string,
  data: {
    serviceId: number;
    tradingStartTime: string | null;
    timezone: string | null;
    defaultPermissionDelayMonths: number | null;
    defaultPermissionDelayDays: number | null;
    defaultPermissionDelayHours: number | null;
    defaultPermissionDelayMinutes: number | null;
  }
) {
  return request(`${ROOT_SERVER_URL}/api/data-feeds/${dataFeedId}/metadata`, {
    data,
    method: 'PUT',
  });
}

export function getDataFeedMetadata(serviceId: number, feedId?: string) {
  const urlSearchParams = collectQueryParams({ serviceId, feedId });
  return request(`${ROOT_SERVER_URL}/api/data-feeds/metadata/?${urlSearchParams}`);
}
